import { clsx } from 'clsx'

export function cn(...inputs) {
  return clsx(inputs)
}

export const PRIORITIES = {
  urgent: { label: 'Urgent', color: '#ef4444' },
  high:   { label: 'High',   color: '#f97316' },
  medium: { label: 'Medium', color: '#eab308' },
  low:    { label: 'Low',    color: '#6b7280' },
  none:   { label: 'None',   color: '#404040' },
}

export const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 }

// Canonical task sort used everywhere tasks render in a group (sections, buckets, columns):
// completed last (most recent first) → priority desc → due date asc (null last) → position asc.
export function sortTasks(tasks) {
  return [...tasks].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1
    if (a.completed && b.completed) return (b.completedAt || '') > (a.completedAt || '') ? 1 : -1
    const pa = PRIORITY_RANK[a.priority] ?? 5
    const pb = PRIORITY_RANK[b.priority] ?? 5
    if (pa !== pb) return pa - pb
    const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
    const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
    if (da !== db) return da - db
    return (a.position ?? 0) - (b.position ?? 0)
  })
}

export function formatDate(iso) {
  if (!iso) return ''
  // Date-only strings (YYYY-MM-DD) must be parsed as local time, not UTC
  const d = new Date(iso.length > 10 ? iso : iso + 'T00:00:00')
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueStart  = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diff = Math.round((dueStart - todayStart) / 86400000)
  if (diff === 0)  return 'Today'
  if (diff === 1)  return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function isOverdue(iso) {
  if (!iso) return false
  return new Date(iso) < new Date() && new Date(iso).getDate() !== new Date().getDate()
}

export function getDueDateBucket(iso) {
  if (!iso) return 'later'
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Parse date string as local time (avoids UTC offset shifting the date)
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const dueStart = new Date(y, m - 1, d)
  const diff = Math.round((dueStart - todayStart) / 86400000)
  if (diff < 0) return 'overdue'
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  // 'week' = within the current calendar week (ends this Saturday)
  // minimum 2 so Fri/Sat still show a useful window
  const dayOfWeek = todayStart.getDay() // 0=Sun … 6=Sat
  const daysToSaturday = (6 - dayOfWeek + 7) % 7 // 0 on Sat, 5 on Mon
  if (diff <= Math.max(daysToSaturday, 2)) return 'week'
  return 'later'
}

export const BUCKET_STYLE = {
  overdue:  { color: '#ff3b3b', bg: 'rgba(255,59,59,0.10)',  dateGlow: '0 0 0 1px rgba(255,59,59,0.28), 0 0 8px rgba(255,59,59,0.22)' },
  today:    { color: '#00d084', bg: 'rgba(0,208,132,0.10)',  dateGlow: '0 0 0 1px rgba(0,208,132,0.28), 0 0 8px rgba(0,208,132,0.22)' },
  tomorrow: { color: '#ff9500', bg: 'rgba(255,149,0,0.10)',  dateGlow: '0 0 0 1px rgba(255,149,0,0.28), 0 0 8px rgba(255,149,0,0.22)' },
  week:     { color: '#3d9bff', bg: 'rgba(61,155,255,0.10)', dateGlow: '0 0 0 1px rgba(61,155,255,0.28), 0 0 8px rgba(61,155,255,0.22)' },
  later:    { color: null, bg: null, dateGlow: null },
}

export function getInitials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

// Recurrence helpers
export const RECURRENCE_OPTIONS = [
  { key: 'none',      label: 'Does not repeat',  value: null },
  { key: 'daily',     label: 'Daily',             value: { freq: 'daily',    interval: 1 } },
  { key: 'weekday',   label: 'Every weekday',     value: { freq: 'weekday',  interval: 1 } },
  { key: 'weekly',    label: 'Weekly',            value: { freq: 'weekly',   interval: 1 } },
  { key: 'biweekly',  label: 'Every 2 weeks',     value: { freq: 'weekly',   interval: 2 } },
  { key: 'monthly',   label: 'Monthly',           value: { freq: 'monthly',  interval: 1 } },
  { key: 'yearly',    label: 'Yearly',            value: { freq: 'yearly',   interval: 1 } },
]

export function recurrenceKey(r) {
  if (!r?.freq) return 'none'
  if (r.freq === 'weekday') return 'weekday'
  if (r.freq === 'weekly' && (r.interval || 1) === 2) return 'biweekly'
  return r.freq
}

export function recurrenceLabel(r) {
  const opt = RECURRENCE_OPTIONS.find((o) => o.key === recurrenceKey(r))
  return opt?.label || 'Does not repeat'
}

export function computeNextDueDate(recurrence, fromDate) {
  if (!recurrence?.freq) return null
  const base = fromDate ? new Date(fromDate + 'T00:00:00') : new Date()
  const d = new Date(base)
  const n = recurrence.interval || 1
  if (recurrence.freq === 'daily')   d.setDate(d.getDate() + n)
  if (recurrence.freq === 'weekday') {
    d.setDate(d.getDate() + 1)
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  }
  if (recurrence.freq === 'weekly')  d.setDate(d.getDate() + n * 7)
  if (recurrence.freq === 'monthly') d.setMonth(d.getMonth() + n)
  if (recurrence.freq === 'yearly')  d.setFullYear(d.getFullYear() + n)
  return d.toISOString().slice(0, 10)
}
